import React, { useState } from 'react';
import Sidebar from './components/sidebar';
import axios from 'axios';
import baseUrl from '../../base_url';



const AdminAddProducts = () => {
  const [product, setProduct] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    numericPrice: '',
    brandName: '',
    articleCode: '',
    broadCategory: 'Jutexx',
    status: 'active',
    featured: false,
    sellingAttribute: 'Default',
    slug: '',
    inventory: '10'
  });

  const [swatches, setSwatches] = useState([]);
  const [newSwatch, setNewSwatch] = useState({
    colorCode: '',
    articleLink: '',
    colorName: ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  
  // For first image metadata
  const [firstImageMetadata, setFirstImageMetadata] = useState({
    alt: '',
    dataAltText: ''
  });

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-') + '-' + product.broadCategory;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setProduct({ ...product, [name]: checked });
    } else {
      const updatedProduct = { ...product, [name]: value };
      
      // Update slug when title changes
      if (name === 'title') {
        updatedProduct.slug = generateSlug(value);
      }
      
      // Update numericPrice when price changes
      if (name === 'price') {
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
        updatedProduct.numericPrice = isNaN(numericValue) ? 0 : numericValue;
      }
      
      setProduct(updatedProduct);
    }
  };

  const handleFirstImageMetadataChange = (e) => {
    const { name, value } = e.target;
    setFirstImageMetadata({ ...firstImageMetadata, [name]: value });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Update selected files
    setSelectedFiles([...selectedFiles, ...files]);
    
    // Create preview URLs
    const newPreviewImages = files.map(file => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...newPreviewImages]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    
    // Also remove the preview URL and revoke it to free memory
    URL.revokeObjectURL(previewImages[index]);
    setPreviewImages(previewImages.filter((_, i) => i !== index));
  };

  const handleSwatchChange = (e) => {
    const { name, value } = e.target;
    setNewSwatch({ ...newSwatch, [name]: value });
  };

  const addSwatch = () => {
    if (newSwatch.colorName && newSwatch.colorCode) {
      setSwatches([...swatches, { ...newSwatch }]);
      setNewSwatch({ colorCode: '', articleLink: '', colorName: '' });
    }
  };

  const removeSwatch = (index) => {
    setSwatches(swatches.filter((_, i) => i !== index));
  };

  const formatPrice = (price) => {
    const numericPrice = parseFloat(price.toString().replace(/[^0-9.]/g, ""));
    return isNaN(numericPrice)
      ? "Rs.0.00"
      : `Rs.${numericPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!product.title || !product.category || !product.price || !product.articleCode) {
      alert('Please fill all required fields: Title, Category, Broad Category, Price, and Article Code');
      return;
    }
    
    if (selectedFiles.length === 0) {
      alert('Please upload at least one product image');
      return;
    }
    
    const formattedPrice = formatPrice(product.price);
    
    // Prepare form data for image uploads
    const formData = new FormData();
    Object.keys(product).forEach(key => {
      if (key === 'price') {
        formData.append(key, formattedPrice);
      } else {
        formData.append(key, product[key]);
      }
    });
    
    // Add first image metadata
    formData.append('alt', firstImageMetadata.alt || `${product.title} Model`);
    formData.append('dataAltText', firstImageMetadata.dataAltText || product.title);
    
    // Add swatches as JSON
    formData.append('swatches', JSON.stringify(swatches));
    
    // Add image files
    selectedFiles.forEach(file => {
      formData.append('images', file);
    });

    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(key, ":", value instanceof File ? `File: ${value.name}` : value);
    }
    
    try {
      // Start loading
      setIsLoading(true);
      setUploadProgress(0);
      setUploadStatus('Preparing to upload product...');
      
      // Use the endpoint with SSE for progress tracking
      const response = await fetch(`${baseUrl}api/admin/add-product-with-progress`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Send authentication cookies
      });
      
      // Create event source reader
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      // Process the stream data
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }
        
        // Decode and parse the SSE data
        const text = decoder.decode(value);
        const lines = text.split('\n\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              // Update progress state
              setUploadProgress(data.progress);
              setUploadStatus(data.message);
              
              // Handle completion
              if (data.status === 'completed') {
                setTimeout(() => {
                  setIsLoading(false);
                  alert('Product added successfully!');
                  window.location.reload();
                }, 500);
              }
              
              // Handle error
              if (data.status === 'error') {
                setIsLoading(false);
                alert(`Error: ${data.message}`);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error adding product:', error);
      alert('Failed to add product. Please try again.');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-700">Add Product</h1>
            <div className="flex gap-2">
              <button 
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => window.location.reload()}
                disabled={isLoading}
              >
                Discard
              </button>
              <button 
                className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          
          {/* Loading Overlay */}
          {isLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">Uploading Product</h3>
                <div className="mb-2 flex justify-between">
                  <span className="text-sm text-gray-600">{uploadStatus}</span>
                  <span className="text-sm font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-200" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-medium mb-4">Basic Information</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="title"
                    placeholder="e.g. Regular Fit Crêpe shirt"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={product.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea
                    name="description"
                    rows="5"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={product.description}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                    <select
                      name="category"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={product.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="laundry">Laundry Bags</option>
                      <option value="bowl">Bowl</option>
                      <option value="matka">Matka</option>
                      <option value="rect">Rect.</option>
                      <option value="ladies">Ladies side bag</option>
                      <option value="mobile">Mobile Bags</option>
                      <option value="tissue">Tissue Boxes</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={product.status}
                      onChange={handleInputChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Broad Category <span className="text-red-500">*</span></label>
                    <select
                      name="category"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={product.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Jutexx">Jutexx</option>
                      <option value="newArrival">New Arrival</option>
                      <option value="Men">Men</option>
                      <option value="Ladies">Ladies</option>
                      <option value="Kids">Kids</option>
                      <option value="Home">Home</option>
                      <option value="Sports">Sports</option>
                      <option value="Baby">Baby</option>
                    </select>
                  </div> */}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  
                  {/* <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selling Attribute</label>
                    <input
                      type="text"
                      name="sellingAttribute"
                      placeholder="e.g. New Arrival, Bestseller"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={product.sellingAttribute}
                      onChange={handleInputChange}
                    />
                  </div> */}
                </div>
              </div>
              
              {/* Product Images */}
              <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-medium mb-4">Product Images <span className="text-red-500">*</span></h2>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-4 text-center">
                  <div className="flex justify-center">
                    <label className="cursor-pointer">
                      <span className="bg-blue-600 text-white px-4 py-2 rounded-md inline-block">Add Images</span>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        required
                      />
                    </label>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">Upload product images. First image will be the main image.</p>
                </div>
                
                {previewImages.length > 0 && (
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                      {previewImages.map((src, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={src}
                            alt={`Preview ${index + 1}`}
                            className={`w-full h-24 object-cover rounded-md ${index === 0 ? 'border-2 border-blue-500' : ''}`}
                          />
                          {index === 0 && (
                            <div className="absolute top-0 left-0 bg-blue-500 text-white text-xs px-1 rounded-br">Main</div>
                          )}
                          <button
                            type="button"
                            className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeFile(index)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {previewImages.length > 0 && (
                      <div className="mt-4 border p-4 rounded-md">
                        <h3 className="text-md font-medium mb-2">Main Image Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
                            <input
                              type="text"
                              name="alt"
                              placeholder="e.g. Regular Fit Shirt Model"
                              className="w-full p-2 border border-gray-300 rounded-md"
                              value={firstImageMetadata.alt}
                              onChange={handleFirstImageMetadataChange}
                            />
                            <p className="text-xs text-gray-500 mt-1">Default: "{product.title} Model"</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Alt Image Text</label>
                            <input
                              type="text"
                              name="dataAltText"
                              placeholder="e.g. Regular Fit Shirt"
                              className="w-full p-2 border border-gray-300 rounded-md"
                              value={firstImageMetadata.dataAltText}
                              onChange={handleFirstImageMetadataChange}
                            />
                            <p className="text-xs text-gray-500 mt-1">Default: "{product.title}"</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Pricing */}
              <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-medium mb-4">Pricing <span className="text-red-500">*</span></h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                      ₹ 
                    </span>
                    <input
                      type="text"
                      name="price"
                      placeholder="1,499.00"
                      className="w-full pl-10 p-2 border border-gray-300 rounded-md"
                      value={product.price}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* Inventory */}
              <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-medium mb-4">Inventory</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Article Code (SKU) <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="articleCode"
                    placeholder="e.g. 1231418002"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={product.articleCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                  <input
                    type="text"
                    name="brandName"
                    placeholder="e.g. Jutexx"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={product.brandName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available in Inventory</label>
                  <input
                    type="number"
                    name="inventory"
                    placeholder="10"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={product.inventory}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              {/* Color Swatches */}
              <div className="bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-medium mb-4">Color Swatches</h2>
                
                <div className="border p-4 rounded-md mb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color Name</label>
                      <input
                        type="text"
                        name="colorName"
                        placeholder="e.g. Burgundy"
                        className="w-full p-2 border border-gray-300 rounded-md"
                        value={newSwatch.colorName}
                        onChange={handleSwatchChange}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color Code</label>
                      <div className="flex">
                        <input
                          type="color"
                          className="h-10 w-10 border border-gray-300 rounded-l-md"
                          value={newSwatch.colorCode}
                          onChange={(e) => setNewSwatch({...newSwatch, colorCode: e.target.value})}
                        />
                        <input
                          type="text"
                          name="colorCode"
                          placeholder="#5B313D"
                          className="flex-1 p-2 border border-gray-300 rounded-r-md"
                          value={newSwatch.colorCode}
                          onChange={handleSwatchChange}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Article Link</label>
                      <input
                        type="text"
                        name="articleLink"
                        placeholder="/en_in/productpage.1231418002.html"
                        className="w-full p-2 border border-gray-300 rounded-md"
                        value={newSwatch.articleLink}
                        onChange={handleSwatchChange}
                      />
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    className="bg-blue-600 text-white px-4 py-2 rounded-md"
                    onClick={addSwatch}
                  >
                    Add Color Swatch
                  </button>
                </div>
                
                {swatches.length > 0 && (
                  <div className="border rounded-md overflow-hidden">
                    <div className="bg-gray-50 border-b p-2 text-sm font-medium grid grid-cols-4">
                      <div>Color</div>
                      <div>Name</div>
                      <div>Code</div>
                      <div>Action</div>
                    </div>
                    <div className="divide-y">
                      {swatches.map((swatch, index) => (
                        <div key={index} className="p-3 grid grid-cols-4 items-center">
                          <div>
                            <div 
                              className="w-6 h-6 rounded-full border border-gray-300"
                              style={{ backgroundColor: swatch.colorCode }}
                            ></div>
                          </div>
                          <div>{swatch.colorName}</div>
                          <div>{swatch.colorCode}</div>
                          <div>
                            <button
                              type="button"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => removeSwatch(index)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-md shadow-sm sticky top-6">
                <h2 className="text-lg font-medium mb-4">Product Summary</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Title</h3>
                    <p className="text-gray-900">{product.title || 'Not set'}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Price</h3>
                    <p className="text-gray-900">
                      {product.price ? formatPrice(product.price) : 'Rs.0.00'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Article Code</h3>
                    <p className="text-gray-900">{product.articleCode || 'Not set'}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Category</h3>
                    <p className="text-gray-900">{product.category || 'Not set'}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Broad Category</h3>
                    <p className="text-gray-900">{product.broadCategory}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Status</h3>
                    <p className="text-gray-900">{product.status}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Selling Attribute</h3>
                    <p className="text-gray-900">{product.sellingAttribute || 'Not set'}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Images</h3>
                    <p className="text-gray-900">{selectedFiles.length} images</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Color Swatches</h3>
                    <p className="text-gray-900">{swatches.length} color variants</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Generated Slug</h3>
                    <p className="text-gray-900 break-all">{product.slug || 'Not set'}</p>
                  </div>
                </div>
                
                <div className="mt-6">
                  <button
                    type="button"
                    className={`w-full px-4 py-2 rounded-md ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Product'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAddProducts;